// /api/custom-mode.js (or wherever you define your API calls)

import { instance } from ".."; // Your axios instance

export const createCutsomQuestion = async (
  modeId,
  webPassword,
  questionInfo
) => {
  const formData = new FormData();

  // Append the text fields
  formData.append("webPassword", webPassword);
  formData.append("question", questionInfo.question);
  formData.append("answer", questionInfo.answer);
  formData.append("points", questionInfo.points);

  // If you have additional fields (marginText, type = "Text", etc.), append those too:
  // formData.append("marginText", questionInfo.marginText ?? "");

  // Append the files (if any)
  if (questionInfo.questionFile) {
    formData.append("questionImage", questionInfo.questionFile);
  }
  if (questionInfo.answerFile) {
    formData.append("answerImage", questionInfo.answerFile);
  }

  // Now submit using axios
  const response = await instance.post(
    `/api/custom-mode/add-web-question/${modeId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

// ============================================================
// Authenticated custom-mode API (community browse + authoring).
// JWT is attached by the axios interceptor.
// ============================================================

const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

// ---- Community browse / discovery ----

// The user's own + added modes, paginated. Pass `q` for server-side name
// search. Returns { data: [...modes], pagination: { total, page, limit,
// totalPages } }. Use with useInfiniteQuery for real pagination.
export const getMyCustomModes = async ({ page = 1, limit = 12, q = "" } = {}) => {
  const { data } = await instance.get("/api/custom-mode/all-paginated", {
    params: { page, limit, ...(q.trim() ? { q: q.trim() } : {}) },
  });
  const items = Array.isArray(data?.data) ? data.data : [];
  return {
    // Back-compat: the endpoint names the count `totalQuestions`.
    data: items.map((m) => ({
      ...m,
      questionsCount: m.questionsCount ?? m.totalQuestions ?? 0,
    })),
    pagination: data?.pagination ?? {
      total: items.length,
      page,
      limit,
      totalPages: 1,
    },
  };
};

export const getCustomModeById = async (id, { lightweight = false } = {}) => {
  const { data } = await instance.get(`/api/custom-mode/${id}`, {
    params: lightweight ? { lightweight: true } : undefined,
  });
  return data;
};

export const getPublishedModes = async (page = 1, limit = 12, sort = "newest") => {
  const { data } = await instance.get("/api/custom-mode/published-modes", {
    params: { page, limit, sort },
  });
  return data;
};

export const searchPublishedModes = async ({
  q = "",
  sortBy = "newest",
  minQuestions = 0,
  minLikes = 0,
  minPlayedInGames = 0,
  minInvitedUsers = 0,
  page = 1,
  limit = 14,
}) => {
  const { data } = await instance.get("/api/custom-mode/search", {
    params: {
      q,
      sortBy,
      minQuestions,
      minLikes,
      minPlayedInGames,
      minInvitedUsers,
      page,
      limit,
    },
  });
  return data;
};

// `liked` / `disliked` (boolean) asks the server to SET that state — safe to
// retry, and liking clears a dislike in the same request. Servers that
// predate it ignore the body and toggle, so only call these when the state
// actually needs to change.
export const likeMode = async (modeId, liked) => {
  const { data } = await instance.put(
    `/api/custom-mode/like/${modeId}`,
    typeof liked === "boolean" ? { liked } : undefined,
  );
  return data; // { liked, likesCount?, dislikesCount? }
};

export const dislikeMode = async (modeId, disliked) => {
  const { data } = await instance.put(
    `/api/custom-mode/dislike/${modeId}`,
    typeof disliked === "boolean" ? { disliked } : undefined,
  );
  return data; // { disliked, likesCount?, dislikesCount? }
};

export const addModeById = async (modeId) => {
  const { data } = await instance.put(`/api/custom-mode/add/${modeId}`);
  return data;
};

export const addModeByInvitation = async (invitationCode) => {
  const { data } = await instance.put(`/api/custom-mode/add`, {
    invitationCode,
  });
  return data;
};

export const removeModeFromUser = async (modeId) => {
  const { data } = await instance.put(`/api/custom-mode/remove/${modeId}`);
  return data;
};

export const toggleCustomModeFavorite = async (modeId, favorited) => {
  const { data } = await instance.post(
    `/api/custom-mode/favorite/${modeId}`,
    typeof favorited === "boolean" ? { favorited } : undefined,
  );
  return data; // { isFavorited, favoritesCount }
};

// ---- Authoring (owner) ----

// modeInfo = { name, info?, category?, imageFile?(File) }
export const createMode = async (modeInfo) => {
  const formData = new FormData();
  formData.append("name", modeInfo.name);
  if (modeInfo.info != null) formData.append("info", modeInfo.info);
  if (modeInfo.category) formData.append("category", modeInfo.category);
  if (modeInfo.imageFile) formData.append("image", modeInfo.imageFile);
  const { data } = await instance.post(
    "/api/custom-mode/create",
    formData,
    MULTIPART
  );
  return data;
};

// Only sends fields that are provided; image only when a new File is picked.
export const updateMode = async (id, modeInfo) => {
  const formData = new FormData();
  if (modeInfo.name != null) formData.append("name", modeInfo.name);
  if (modeInfo.info != null) formData.append("info", modeInfo.info);
  if (modeInfo.category) formData.append("category", modeInfo.category);
  if (modeInfo.imageFile) formData.append("image", modeInfo.imageFile);
  const { data } = await instance.put(
    `/api/custom-mode/update/${id}`,
    formData,
    MULTIPART
  );
  return data;
};

export const deleteMode = async (id) => {
  const { data } = await instance.delete(`/api/custom-mode/delete/${id}`);
  return data;
};

export const publishMode = async (modeId) => {
  const { data } = await instance.put(`/api/custom-mode/publish/${modeId}`);
  return data;
};

export const unpublishMode = async (modeId) => {
  const { data } = await instance.put(`/api/custom-mode/unpublish/${modeId}`);
  return data;
};

// Note: not named `useWeb` so it isn't mistaken for a React hook (rules-of-hooks).
export const enableModeWeb = async (modeId, webPassword) => {
  const { data } = await instance.put(`/api/custom-mode/use-web/${modeId}`, {
    webPassword,
  });
  return data;
};

export const dontUseWeb = async (modeId) => {
  const { data } = await instance.put(`/api/custom-mode/not-use-web/${modeId}`);
  return data;
};

// ---- Questions ----

export const getModeQuestions = async (modeId, { page = 1, limit = 20 } = {}) => {
  const { data } = await instance.get(`/api/custom-mode/${modeId}/questions`, {
    params: { page, limit },
  });
  return data;
};

// Appends text fields + optional image File objects to FormData.
const buildQuestionForm = (questionInfo) => {
  const formData = new FormData();
  const fileFields = ["questionImage", "answerImage"];
  for (const key in questionInfo) {
    if (fileFields.includes(key)) continue;
    if (Array.isArray(questionInfo[key])) {
      questionInfo[key].forEach((item, i) =>
        formData.append(`${key}[${i}]`, item)
      );
    } else if (questionInfo[key] != null) {
      formData.append(key, questionInfo[key]);
    }
  }
  if (questionInfo.questionImage instanceof File)
    formData.append("questionImage", questionInfo.questionImage);
  if (questionInfo.answerImage instanceof File)
    formData.append("answerImage", questionInfo.answerImage);
  return formData;
};

// questionInfo = { question, answer, points, wrongAnswers?[], marginText?,
//   questionImage?(File), answerImage?(File) }
export const createQuestion = async (modeId, questionInfo) => {
  const { data } = await instance.post(
    `/api/custom-mode/${modeId}/questions`,
    buildQuestionForm(questionInfo),
    MULTIPART
  );
  return data;
};

export const updateQuestion = async (questionId, questionInfo) => {
  const { data } = await instance.put(
    `/api/custom-mode/update-question/${questionId}`,
    buildQuestionForm(questionInfo),
    MULTIPART
  );
  return data;
};

export const deleteQuestion = async (questionId) => {
  const { data } = await instance.delete(
    `/api/custom-mode/delete-question/${questionId}`
  );
  return data;
};
